import { useCallback, useState } from 'react'
import { RcFile } from 'antd/lib/upload'
import { LIST_IGNORE } from 'antd/lib/upload/Upload'
import { message } from 'antd'
import { CompressorOptionsProps, getImageSizes } from '~/utils'
import { FileType } from '~/configs'
import {
  OssBucket,
  Scene,
  imageDetection,
  imageDetectionVariables,
} from '~/graphqls/types'
import { uploadQiNiu } from '~/apis/upload-oss'
import { useMutation } from '@apollo/client'
import { IMAGE_DETECTION } from '~/graphqls/gqls'

interface OptionsProps {
  onUrlChange?: (url: string, involveYellow?: boolean) => void
  /** 大小限制，单位 M */
  limit?: number
  // 默认值TRADE_PROTECT（开启原图保护），作用于作品
  bucket?: OssBucket
  /** 是否显示上传列表 */
  showList?: boolean
  /** 剩余可上传数量 */
  remainToken?: number
  /** 支持文件类型 */
  fileTypes?: string[]
  dirname?: string
  getImageSize?: ({ width, height }: { width: number; height: number }) => void
  compressOptions?: CompressorOptionsProps
  isCompress?: boolean
  /** 是否检测图片违规 */
  isDetectImage?: boolean
}

export const useUpload = (options?: OptionsProps) => {
  const {
    onUrlChange,
    limit = 2,
    bucket = OssBucket.TRADE_PROTECT,
    showList = false,
    remainToken = Infinity,
    fileTypes = [FileType.jpg, FileType.png, FileType.webp],
    getImageSize,
    compressOptions,
    isCompress = true,
    isDetectImage = false,
  } = options || {}

  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  let _remainToken = remainToken

  const [checkImage] = useMutation<imageDetection, imageDetectionVariables>(
    IMAGE_DETECTION
  )

  const handleDetectionImage = async (url: string) => {
    try {
      await checkImage({
        variables: {
          input: {
            image: {
              url,
            },
            needSyncReturn: true,
            scene: Scene.SQUARE_POST,
          },
        },
      })
      onUrlChange?.(url, false)
    } catch {
      onUrlChange?.(url, true)
    }
  }

  const beforeUpload = useCallback(
    async (file: RcFile) => {
      console.log(file.type, 'beforeUpload.file.type')
      if (!fileTypes.includes(file.type)) {
        message.destroy()
        message.error('不支持该文件类型')
        return showList ? LIST_IGNORE : Promise.reject()
      }

      const isLimit = file.size / 1024 / 1024 < limit
      if (!isLimit) {
        message.destroy()
        message.error(`请上传 ${limit}M 内的文件`)
        return showList ? LIST_IGNORE : Promise.reject()
      }

      if (!!getImageSize) {
        getImageSizes(file, ({ width, height }) => {
          getImageSize({ width, height })
        })
      }

      _remainToken -= 1
      if (_remainToken < 0) {
        return showList ? LIST_IGNORE : Promise.reject()
      }
    },
    [limit, remainToken]
  )

  const customRequest = async (options: any) => {
    console.log(options, 'customRequest.options')
    setLoading(true)
    try {
      const { url } = await uploadQiNiu(
        options.file,
        undefined,
        bucket,
        undefined,
        {
          compressOptions,
          isCompress,
          onPregress({ total }) {
            setProgress(Math.floor(total.percent || 0))
          },
        }
      )
      if (isDetectImage) {
        handleDetectionImage(url)
      } else {
        onUrlChange?.(url)
      }
      setProgress(0)
    } catch (err) {
      console.log(err, 'customRequest.err')
      message.destroy()
      message.error('上传异常，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    beforeUpload,
    progress,
    customRequest,
  }
}
